/* ------------------------------------------------------------------------
 * Access gate — the form. The decision is on the server.
 *
 * This module used to hold the access codes, the master password and the key
 * it signed demo links with, and it compared them here in the browser. All
 * three were downloaded by every visitor, so anyone could read them out of the
 * sources panel, mint a demo link with any expiry they wanted, or call the
 * function that opened the gate. It scoped a demo link. It was not a control,
 * and it could not have been: everything it checked against was already in the
 * caller's hands.
 *
 * The codes now live in the backend's environment and never leave it. This
 * renders the gate, posts what the user typed to src/lib/session.js, and shows
 * whatever comes back. It does not know what a valid code looks like.
 *
 * That also means the three redemption paths are asynchronous now, where they
 * used to be a string comparison. The handlers disable their button while a
 * check is in flight and surface the server's message on failure — including
 * "too many attempts", which the old version had no concept of.
 *
 * Cross-module calls still go through the `window.*` registry the original
 * used. That is deliberate: rewiring ~190 functions into an import graph is a
 * separate change with its own risk. See MIGRATION.md.
 *
 * Originally from index.html, script block 4.
 * ---------------------------------------------------------------------- */

// ===== SENTRIPOL ACCESS CONTROL =====
// The decision is the server's. This renders the form and shows the answer.
//
// What used to be here: SP_ACCESS_CODES, SP_MASTER, and SP_SECRET with a
// hand-rolled 32-bit string hash standing in for a signature. All three were
// downloaded by every visitor. Anyone could read the codes, and spSign was
// reversible enough that anyone could mint a demo token with any label and any
// expiry. None of it is here now, and none of it is in the bundle.
//
// src/lib/session.js talks to the server and holds the resulting token.

import * as session from './session.js';

/* Escape before innerHTML. The only value that reaches it here is an error
 * message this app produced, but "it is ours" is how the next value that is
 * not ours gets through unescaped. */
function spEscapeText(v) {
  return String(v == null ? '' : v).replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]
  );
}

// ----- CHECK URL ON LOAD -----
// A ?demo= value is a token the server signed. The browser cannot read it and
// does not try; it hands it back and is told what it means.
async function spCheckUrl() {
  // A Microsoft sign-in comes first: finishing the /auth/callback redirect, or
  // an account already in this tab's MSAL cache. A broker signed in with
  // Microsoft gets their full session even if they arrived by a trial link.
  try {
    const entra = await session.initEntra();
    if (entra) {
      if (window.location.pathname === '/auth/callback') {
        window.history.replaceState({}, '', '/');
      }
      spStartSession(entra.label, null);
      document.getElementById('spGate').classList.add('hidden');
      if (session.isAdmin()) spAdminView(true);
      return true;
    }
  } catch (e) {
    const err = document.getElementById('spError');
    if (err) err.textContent = 'Microsoft sign-in did not complete: ' + (e && e.message ? e.message : 'unknown error') + ' Try again, or use an access code.';
  }

  const params = new URLSearchParams(window.location.search);

  // ?trial is the public way in: "See a live analysis" on sentripol.ai links
  // here. The server says whether trials are open; if they are not, or today's
  // allowance is spent, the gate stays up with its reason.
  if (params.has('trial')) {
    try {
      const s = await session.startTrial();
      spStartSession(s.label, Date.parse(s.expires_at));
      document.getElementById('spGate').classList.add('hidden');
      return true;
    } catch (e) {
      const err = document.getElementById('spError');
      if (err) err.textContent = e.message + ' To see SentriPol on your own policies, book a call at calendly.com/dayna-sentripol.';
      return false;
    }
  }

  const token = params.get('demo');
  if (!token) return false;

  try {
    const s = await session.redeemDemoToken(token);
    spStartSession('Demo: ' + s.label, Date.parse(s.expires_at));
    document.getElementById('spGate').classList.add('hidden');
    return true;
  } catch {
    // Expired and forged land in the same place, deliberately. The server does
    // not distinguish them in its reply and neither should this.
    document.getElementById('spExpired').classList.remove('hidden');
    return 'expired';
  }
}

// ----- PASSWORD VERIFICATION -----
async function spVerify() {
  const field = document.getElementById('spCode');
  const btn = document.getElementById('spVerifyBtn');
  const err = document.getElementById('spError');
  const input = field.value.trim();
  if (!input) { err.textContent = 'Please enter an access code.'; return; }

  // The check is a round trip now, so the button has to say so and stop taking
  // a second click while the first is still in flight.
  err.textContent = '';
  btn.disabled = true;
  const label = btn.textContent;
  btn.textContent = 'Checking\u2026';
  try {
    const s = await session.redeemCode(input);
    field.value = '';
    document.getElementById('spGate').classList.add('hidden');
    spStartSession(s.label, Date.parse(s.expires_at));
  } catch (e) {
    // The server's own words: it distinguishes a bad code from too many
    // attempts, and the second is worth telling someone about.
    err.textContent = e.message + ' Contact dayna.stewart@gmail.com for access.';
  } finally {
    btn.disabled = false;
    btn.textContent = label;
  }
}

// ----- v2.18 RECORDING-MODE AUTOFILL -----
// When session label === 'Recording Mode', a small "Load Demo Data" button
// appears next to the upload zone. Clicking it pre-fills the form with a
// fictional org from DEMO_PROFILES so the founder can record screen demos
// without typing real client information on camera.
function spIsRecordingMode() {
  return window._spSessionLabel === 'Recording Mode';
}

function spLoadDemoData() {
  const demo = DEMO_PROFILES[Math.floor(Math.random() * DEMO_PROFILES.length)];
  // Set policyholder name
  const nameEl = document.getElementById('policyholderName');
  if (nameEl) { nameEl.value = demo.name; }
  // Set website
  const urlEl = document.getElementById('websiteUrl');
  if (urlEl) { urlEl.value = demo.website; }
  // Set case nickname
  const nickEl = document.getElementById('caseNick');
  if (nickEl) { nickEl.value = demo.nick + ' Demo'; }
  // v2.19: Track the active demo org name so the live-redactor knows what to swap to
  window._spDemoActiveName = demo.name;
  showToast('Demo data loaded: ' + demo.name, 'success');
  // v2.19: If results are already on screen (re-run scenario), redact immediately
  spDemoLiveRedact();
}

function spInjectRecordingButton() {
  if (!spIsRecordingMode()) return;
  // Avoid duplicate injection
  if (document.getElementById('spDemoLoadBtn')) return;
  // Find a sensible insertion point — near the policyholder name field
  const anchor = document.getElementById('policyholderName');
  if (!anchor || !anchor.parentElement) return;

  // Row 1: Load Demo Data button + hint
  const wrap = document.createElement('div');
  wrap.style.cssText = 'margin-top:6px; display:flex; align-items:center; gap:8px; flex-wrap:wrap;';
  const btn = document.createElement('button');
  btn.id = 'spDemoLoadBtn';
  btn.type = 'button';
  btn.textContent = '\uD83C\uDFAC  Load Demo Data';
  btn.style.cssText = 'padding:6px 12px; background:#003366; color:#fff; border:none; border-radius:4px; font-size:12px; font-weight:600; cursor:pointer; letter-spacing:0.02em;';
  btn.onclick = spLoadDemoData;
  const hint = document.createElement('span');
  hint.style.cssText = 'font-size:11px; color:#888;';
  hint.textContent = 'Recording mode \u2014 fills with fictional org for screen demos';
  wrap.appendChild(btn);
  wrap.appendChild(hint);
  anchor.parentElement.appendChild(wrap);

  // v2.19: Row 2 \u2014 live redaction terms (replaces real org tokens in rendered output)
  const redactWrap = document.createElement('div');
  redactWrap.style.cssText = 'margin-top:6px; display:flex; align-items:center; gap:8px; flex-wrap:wrap;';
  const redactLabel = document.createElement('label');
  redactLabel.style.cssText = 'font-size:11px; color:#555; font-weight:600;';
  redactLabel.textContent = 'Live redact terms:';
  redactLabel.htmlFor = 'spDemoRedactInput';
  const redactInput = document.createElement('input');
  redactInput.id = 'spDemoRedactInput';
  redactInput.type = 'text';
  redactInput.value = 'ASTEC';
  redactInput.placeholder = 'e.g. ASTEC, Astec Inc';
  redactInput.style.cssText = 'padding:4px 8px; font-size:12px; border:1px solid #ccc; border-radius:3px; min-width:200px;';
  redactInput.addEventListener('input', spDemoLiveRedact);
  const redactHint = document.createElement('span');
  redactHint.style.cssText = 'font-size:11px; color:#888;';
  redactHint.textContent = 'Comma-separated. Replaced with demo org name in displayed output.';
  redactWrap.appendChild(redactLabel);
  redactWrap.appendChild(redactInput);
  redactWrap.appendChild(redactHint);
  anchor.parentElement.appendChild(redactWrap);

  // v2.19.1: Hide/Show demo controls toggle \u2014 keeps the demo machinery off-camera
  const hideWrap = document.createElement('div');
  hideWrap.style.cssText = 'margin-top:6px;';
  const hideLink = document.createElement('a');
  hideLink.id = 'spDemoHideToggle';
  hideLink.href = '#';
  hideLink.textContent = 'Hide demo controls (for recording)';
  hideLink.style.cssText = 'font-size:11px; color:#003366; text-decoration:underline; cursor:pointer;';
  hideLink.addEventListener('click', function(e) {
    e.preventDefault();
    const hidden = wrap.style.display === 'none';
    wrap.style.display = hidden ? 'flex' : 'none';
    redactWrap.style.display = hidden ? 'flex' : 'none';
    hideLink.textContent = hidden ? 'Hide demo controls (for recording)' : 'Show demo controls';
  });
  hideWrap.appendChild(hideLink);
  anchor.parentElement.appendChild(hideWrap);

  // v2.19: Start the live-redaction MutationObserver
  spStartDemoObserver();
}

// ----- v2.19 LIVE DE-IDENTIFICATION (RECORDING MODE) -----
// In recording mode, walks the rendered result tabs and the report title,
// replacing any occurrence of user-specified real-world tokens (e.g. "ASTEC")
// with the currently active demo org name. Triggered by a MutationObserver
// so it runs every time the analysis output changes \u2014 keeping the screen
// camera-safe in real time, no matter where the token appears.
window._spDemoActiveName = null;
window._spDemoObserverActive = false;
window._spDemoRedactBusy = false;

function spDemoGetRedactTerms() {
  const el = document.getElementById('spDemoRedactInput');
  if (!el) return [];
  return el.value.split(',').map(s => s.trim()).filter(s => s.length >= 2);
}

function spDemoLiveRedact() {
  if (!spIsRecordingMode()) return;
  if (window._spDemoRedactBusy) return;
  // Prefer the org name set by Load Demo Data; fall back to whatever
  // is currently in the policyholderName input so manual typers work too.
  let demoName = window._spDemoActiveName;
  if (!demoName) {
    const nameEl = document.getElementById('policyholderName');
    if (nameEl && nameEl.value && nameEl.value.trim().length >= 2) {
      demoName = nameEl.value.trim();
    }
  }
  if (!demoName) return;
  const terms = spDemoGetRedactTerms();
  if (!terms.length) return;
  window._spDemoRedactBusy = true;
  try {
    // Build one combined regex (case-insensitive, longest term first to avoid partial overlaps)
    const sorted = terms.slice().sort((a,b) => b.length - a.length);
    const pattern = new RegExp('(' + sorted.map(escapeRegex).join('|') + ')', 'gi');

    // v2.19.1: Scan the ENTIRE body \u2014 modals, tooltips, tabs, hover states, everything.
    // Skip the form inputs (so the user's own policyholderName field isn't rewritten
    // to itself in a loop) and skip the demo controls row.
    const targets = [document.body];

    // Browser tab title
    if (document.title && pattern.test(document.title)) {
      document.title = document.title.replace(pattern, demoName);
    }

    // IDs/areas to skip so we don't clobber the user's own form inputs and demo controls
    const skipIds = new Set(['policyholderName','caseNick','websiteUrl','orgOverview','states','incumbent','spDemoRedactInput','spDemoLoadBtn','spDemoHideToggle']);

    targets.forEach(root => {
      // Walk text nodes only \u2014 keeps HTML/IDs/classes intact
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode(node) {
          if (!node.nodeValue) return NodeFilter.FILTER_REJECT;
          if (!pattern.test(node.nodeValue)) return NodeFilter.FILTER_REJECT;
          // Walk up to see if this node is inside a skipped form field or the gate screen
          let p = node.parentElement;
          while (p) {
            if (p.id && skipIds.has(p.id)) return NodeFilter.FILTER_REJECT;
            if (p.id === 'spGate' || p.id === 'spAdmin') return NodeFilter.FILTER_REJECT;
            p = p.parentElement;
          }
          return NodeFilter.FILTER_ACCEPT;
        }
      });
      const toFix = [];
      let n;
      while ((n = walker.nextNode())) toFix.push(n);
      toFix.forEach(node => {
        node.nodeValue = node.nodeValue.replace(pattern, demoName);
      });
      // Also patch attributes that often surface on screen: title, alt, placeholder, aria-label
      // (Excluding 'value' since that's the form input the user is typing into.)
      root.querySelectorAll('[title],[alt],[placeholder],[aria-label]').forEach(el => {
        if (el.id && skipIds.has(el.id)) return;
        ['title','alt','placeholder','aria-label'].forEach(attr => {
          if (el.hasAttribute(attr)) {
            const v = el.getAttribute(attr);
            if (v && pattern.test(v)) el.setAttribute(attr, v.replace(pattern, demoName));
          }
        });
      });
    });
  } catch (e) {
    // Fail silently \u2014 redaction is best-effort
  } finally {
    window._spDemoRedactBusy = false;
  }
}

function spStartDemoObserver() {
  if (window._spDemoObserverActive) return;
  window._spDemoObserverActive = true;
  let pending = false;
  const debounced = () => {
    if (pending) return;
    pending = true;
    requestAnimationFrame(() => {
      pending = false;
      spDemoLiveRedact();
    });
  };
  // v2.19.1: Observe the whole body so modals, popups, dynamic sections are all caught
  const obs = new MutationObserver(debounced);
  obs.observe(document.body, { childList: true, subtree: true, characterData: true });
  // Run once immediately in case content already rendered
  spDemoLiveRedact();
  // And run again after a short delay to catch late-binding content
  setTimeout(spDemoLiveRedact, 500);
  setTimeout(spDemoLiveRedact, 1500);
}

// ----- ADMIN -----
async function spAdminLogin() {
  const field = document.getElementById('spMaster');
  const btn = document.getElementById('spAdminLoginBtn');
  const err = document.getElementById('spAdminError');
  const input = field.value.trim();
  if (!input) { err.textContent = 'Please enter the master password.'; return; }

  err.textContent = '';
  btn.disabled = true;
  const label = btn.textContent;
  btn.textContent = 'Checking\u2026';
  try {
    await session.adminLogin(input);
    field.value = '';
    spEnterAdminView();
  } catch (e) {
    err.textContent = e.message;
  } finally {
    btn.disabled = false;
    btn.textContent = label;
  }
}

function spOpenAdmin() {
  document.getElementById('spAdmin').classList.remove('hidden');
  document.getElementById('spGate').classList.add('hidden');
  /* The panel used to print every access code on screen, read straight out of
   * the array it compared against. Both are gone: the codes are on the server
   * and it does not hand them out, because a list of valid codes is the one
   * thing a login screen must never be able to show. Whoever administers them
   * sets SP_ACCESS_CODES; this panel mints links. */
  const codesEl = document.getElementById('spAdminCodes');
  if (codesEl) {
    codesEl.innerHTML =
      '<div class="sp-admin-code-item"><span>Access codes are held by the ' +
      'server and set with SP_ACCESS_CODES. They are not listed here.</span></div>';
  }
}

function spCloseAdmin() {
  document.getElementById('spAdmin').classList.add('hidden');
  // An administrator opens this panel from the app's tabs and goes back to
  // them. Anyone else only ever reached it from the gate.
  if (!session.isAdmin()) document.getElementById('spGate').classList.remove('hidden');
}

/* The administrator's view: the app and the cost dashboard as two tabs in the
 * masthead, so both stay a click away for the whole session. App.jsx owns the
 * tabs and listens for this; the server still refuses /api/costs to anyone
 * who is not an administrator, whatever is shown. */
function spAdminView(on) {
  window.dispatchEvent(new CustomEvent('sp:admin-view', { detail: { on: Boolean(on) } }));
}

function spEnterAdminView() {
  document.getElementById('spAdmin').classList.add('hidden');
  document.getElementById('spGate').classList.add('hidden');
  spStartSession('Administrator', null);
  spAdminView(true);
}

async function spGenLink() {
  const label = document.getElementById('spLinkLabel').value.trim();
  const days = parseInt(document.getElementById('spLinkDays').value) || 3;
  if (!label) { alert('Please enter a recipient name.'); return; }

  // The server signs the link. When this was done here, the signing key was in
  // the bundle, so a link was something any visitor could issue themselves.
  let url;
  try {
    url = await session.createDemoLink(label, days);
  } catch (e) {
    document.getElementById('spLinkOutput').innerHTML =
      '<div class="sp-link-output">' + spEscapeText(e.message) + '</div>';
    return;
  }
  const expDate = new Date(Date.now() + days * 86400000).toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' });

  document.getElementById('spLinkOutput').innerHTML =
    '<div class="sp-link-output">' +
    '<strong>Demo link for ' + label + ':</strong><br><br>' +
    '<input type="text" value="' + url + '" style="width:100%;padding:8px;border:1px solid #86EFAC;border-radius:4px;font-size:11px;margin:4px 0;font-family:monospace;" readonly onclick="this.select()">' +
    '<br>Expires: ' + expDate + ' (' + days + ' days)' +
    '<br><button class="sp-gate-btn" style="width:auto;padding:8px 16px;font-size:12px;margin-top:8px;" onclick="navigator.clipboard.writeText(\'' + url + '\');this.textContent=\'Copied!\';">Copy Link</button>' +
    '</div>';
  document.getElementById('spLinkLabel').value = '';
}

// ----- SESSION -----
let spTimerInterval = null;
// v2.18: Capture session label so recording-mode features know to activate
function spStartSession(label, expiresMs) {
  window._spSessionLabel = label;
  // Trial sessions hide the exports and staff tools (styles.css, body.sp-trial);
  // the engine checks window.spIsTrialSession to limit the report itself.
  document.body.classList.toggle('sp-trial', session.isTrial());
  const bar = document.getElementById('spSession');
  document.getElementById('spSessionLabel').textContent = label;
  bar.classList.remove('hidden');
  // v2.18: Inject the "Load Demo Data" button if this session is recording mode
  setTimeout(spInjectRecordingButton, 100);

  if (expiresMs) {
    function updateTimer() {
      const diff = expiresMs - Date.now();
      if (diff <= 0) { spLogout(); document.getElementById('spExpired').classList.remove('hidden'); return; }
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      document.getElementById('spSessionTimer').textContent = (d > 0 ? d + 'd ' : '') + h + 'h ' + m + 'm remaining';
    }
    updateTimer();
    spTimerInterval = setInterval(updateTimer, 60000);
  }
}

function spLogout() {
  if (spTimerInterval) clearInterval(spTimerInterval);
  // A Microsoft sign-in is ended at Microsoft too (redirects back to /).
  if (session.isEntra()) { session.logout(); return; }
  document.body.classList.remove('sp-trial');
  spAdminView(false);
  session.clear();
  document.getElementById('spSession').classList.add('hidden');
  document.getElementById('spAdmin').classList.add('hidden');
  document.getElementById('spGate').classList.remove('hidden');
  document.getElementById('spCode').value = '';
  if (window.location.search.includes('demo=') || new URLSearchParams(window.location.search).has('trial')) {
    window.history.replaceState({}, '', window.location.pathname);
  }
}

function spToggleMode() {
  const login = document.getElementById('spGateLogin');
  const admin = document.getElementById('spGateAdmin');
  const toggle = document.getElementById('spModeToggle');
  if (login.style.display !== 'none') {
    login.style.display = 'none'; admin.style.display = 'block';
    toggle.textContent = 'Back to Login';
  } else {
    login.style.display = 'block'; admin.style.display = 'none';
    toggle.textContent = 'Administrator Access';
  }
}

// The engine reads this through the window registry, like everything else it
// calls across modules, and stops the Full Review after section 4 when it is
// true. This is a presentation limit, not a control: the analysis runs in the
// browser, so a visitor who replaces this function from the console gets the
// whole report built locally. What the server does enforce is the cost — the
// trial's routes and caps in services/backend/src/trial.js.
window.spIsTrialSession = session.isTrial;

// ----- INIT -----
(function() {
  const result = spCheckUrl();
  if (result === true) {
    const g = document.getElementById('spGate');
    if (g) g.classList.add('hidden');
  }
})();

// ----- EVENT LISTENERS (no inline handlers) -----
// Queued rather than bound: React mounts the gate markup, so none of these
// elements exist when the module is imported, and getElementById returns null.
// spWireGate() binds the queue once the markup is up; it is idempotent.
const _spGatePending = [];
function spGateOn(id, ev, fn) { _spGatePending.push([id, ev, fn]); }
export function spWireGate() {
  let bound = 0;
  _spGatePending.forEach(([id, ev, fn]) => {
    const el = typeof document !== 'undefined' && document.getElementById(id);
    if (!el) return;
    const flag = '_spGateBound_' + ev;
    if (el[flag]) return;
    el[flag] = 1;
    el.addEventListener(ev, fn);
    bound++;
  });
  return bound;
}

spGateOn('spMicrosoftBtn', 'click', function() {
  const err = document.getElementById('spError');
  if (err) err.textContent = '';
  session.loginWithMicrosoft().catch(function(e) {
    if (err) err.textContent = 'Could not start Microsoft sign-in: ' + (e && e.message ? e.message : 'unknown error');
  });
});
spGateOn('spVerifyBtn', 'click', spVerify);
spGateOn('spCode', 'keydown', function(e) { if(e.key==='Enter') spVerify(); });
spGateOn('spAdminLoginBtn', 'click', spAdminLogin);
spGateOn('spMaster', 'keydown', function(e) { if(e.key==='Enter') spAdminLogin(); });
spGateOn('spModeToggle', 'click', spToggleMode);
spGateOn('spLogoutBtn', 'click', spLogout);
spGateOn('spAdminCloseBtn', 'click', spCloseAdmin);
spGateOn('spGenLinkBtn', 'click', spGenLink);

/* Named exports for the tests and the React layer. The window.*
 * registrations inside stay — they are how the modules find each other. */
export {
  spEscapeText,
  spAdminLogin,
  spCheckUrl,
  spCloseAdmin,
  spDemoGetRedactTerms,
  spEnterAdminView,
  spDemoLiveRedact,
  spGenLink,
  spInjectRecordingButton,
  spIsRecordingMode,
  spLoadDemoData,
  spLogout,
  spOpenAdmin,
  spStartDemoObserver,
  spStartSession,
  spToggleMode,
  spVerify,
};
